/** @jsx jsx */
import { InfoBox, WysiwygViewer } from "@eworkit/iris";
import { isEmpty } from "lodash-es";
import { FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";
import { jsx } from "theme-ui";

import { CardSection, DetailSection } from "@/components/Section";
import { UserContext } from "@/components/User";
import { MEDIA_SIZE_SM } from "@/core/media";
import { getJobRequestClientInfo, IJobRequest } from "@/data/job-requests";
import { Flex } from "@/eworkitComponents";
import { isHtmlEmpty } from "@/eworkitMove/HtmlRenderer";
import { SessionContext } from "@/infrastructure/Session";
import { useRouter } from "@/router/useRouter";
import { AttachmentsList } from "@/views/components/Attachments";
import { PublicAttachmentsList } from "@/views/components/Attachments/PublicAttachmentsList";
import { ContactPersonDetail } from "@/views/components/Contact/ContactPersonDetail";
import { CustomField, CustomFields } from "@/views/components/CustomFields";
import { JobRequestLanguages, JobRequestSkills } from "@/views/components/JobRequests/Partials";
import { PayexpressBanner } from "@/views/job-requests/_components/PayexpressBanner";
import { JobRequestManagedByDetailSection } from "@/views/JobRequests/Detail/components/JobRequestManagerByDetailSection";
import { JobRequestAdditionalDetailSections } from "@/views/JobRequests/Detail/JobRequestAdditionalDetailSections";
import { JobRequestDetailSections } from "@/views/JobRequests/Detail/JobRequestDetailSections";
import { JobRequestPublicCreateAccountSection } from "@/views/JobRequests/Detail/JobRequestPublicCreateAccountSection";

interface JobRequestDetailMainSectionProps {
  payload: IJobRequest;
}

export const JobRequestDetailMainSection: FC<JobRequestDetailMainSectionProps> = ({ payload }) => {
  const { t } = useTranslation("session");
  const { authenticated } = useContext(SessionContext);

  const requiredSkillsEmpty = isEmpty(payload.skills.filter(skill => skill.priority === "REQUIRED"));
  const preferredSkillsEmpty = isEmpty(payload.skills.filter(skill => skill.priority === "PREFERRED"));
  const languagesEmpty = isEmpty(payload.languages);

  const descriptionEmpty = isHtmlEmpty(payload.description);
  const requirementsEmpty = isHtmlEmpty(payload.requirements);
  const attachmentsEmpty = isEmpty(payload.attachments);
  const customFieldsEmpty = isEmpty(payload.customFields);
  const rateEmpty = isEmpty(payload.rate) || !payload.rate.maxRate;
  const referenceIdEmpty = isEmpty(payload.businessReferenceId);
  const contactsEmpty = isEmpty(payload.hiringManager);
  const originEmpty = isEmpty(payload.origin);
  const additionalDetailsEmpty =
    isEmpty(payload.promoteClient) &&
    isEmpty(payload.client) &&
    isEmpty(payload.origin.name) &&
    isEmpty(payload.firstDayOfApplications) &&
    rateEmpty &&
    isEmpty(payload.numberOfPositions) &&
    isEmpty(payload.skillRole) &&
    isEmpty(payload.level) &&
    isEmpty(payload.businessUnitName) &&
    isEmpty(payload.legalEntityName) &&
    isEmpty(payload.legalEntityVatNo) &&
    isEmpty(payload.hoursPerWeek);

  const baseClass = "job-request-detail";
  const reactRouter = useRouter<{}, Record<string, string>>();
  const blurSwitchParamName = "noBlur";
  const blurSwitch = reactRouter.searchParams[blurSwitchParamName];

  const { administratorName } = getJobRequestClientInfo(payload);
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const { contextCompany } = useContext(UserContext);

  return (
    <Flex innerMargin="md" direction="column">
      <CardSection
        border={[false, true, false, true]}
        padding={isSmallScreen ? ["md"] : ["lg"]}
        innerMargin="lg"
        flex
        className={!authenticated && !blurSwitch ? `${baseClass}__public` : undefined}>
        <JobRequestDetailSections payload={payload} />
        <Flex
          className={!authenticated && !blurSwitch ? `${baseClass}__public_blurred` : undefined}
          innerMargin="md"
          direction="column">
          <DetailSection
            className={`${baseClass}__section`}
            isVisible={!descriptionEmpty}
            label={t("JOB_REQUESTS.DETAIL.SECTION_HEADER_DESCRIPTION")}
            weight="bold">
            <WysiwygViewer value={payload.description ?? ""} />
          </DetailSection>

          <DetailSection
            isVisible={!requiredSkillsEmpty}
            label={t("JOB_REQUESTS.DETAIL.SECTION_HEADER_REQUIRED_SKILLS")}
            weight="bold">
            <JobRequestSkills
              skills={payload.skills.filter(skill => skill.priority === "REQUIRED")}
              showMore={true}
              visibleSkillsCount={15}
            />
          </DetailSection>

          <DetailSection
            className={`${baseClass}__section`}
            isVisible={!preferredSkillsEmpty}
            label={t("JOB_REQUESTS.DETAIL.SECTION_HEADER_PREFERRED_SKILLS")}
            weight="bold">
            <JobRequestSkills
              skills={payload.skills.filter(skill => skill.priority === "PREFERRED")}
              showMore={true}
              visibleSkillsCount={15}
            />
          </DetailSection>

          <DetailSection
            className={`${baseClass}__section`}
            isVisible={!languagesEmpty}
            label={t("JOB_REQUESTS.DETAIL.SECTION_HEADER_LANGUAGES")}
            weight="bold">
            <JobRequestLanguages languages={payload.languages} />
          </DetailSection>

          <DetailSection
            className={`${baseClass}__section`}
            isVisible={!requirementsEmpty}
            label={t("JOB_REQUESTS.DETAIL.SECTION_HEADER_REQUIREMENTS")}
            weight="bold">
            <WysiwygViewer value={payload.requirements ?? ""} />
          </DetailSection>

          <DetailSection
            className={`${baseClass}__section`}
            isVisible={!isHtmlEmpty(payload.skillRoleDescription)}
            label={t("JOB_REQUESTS.DETAIL.SECTION_HEADER_ROLE_DESCRIPTION")}
            weight="bold">
            <WysiwygViewer value={payload.skillRoleDescription ?? ""} />
          </DetailSection>

          <DetailSection
            className={`${baseClass}__section`}
            isVisible={!isHtmlEmpty(payload.levelDescription)}
            label={t("JOB_REQUESTS.DETAIL.SECTION_HEADER_LEVEL_DESCRIPTION")}
            weight="bold">
            <WysiwygViewer value={payload.levelDescription ?? ""} />
          </DetailSection>

          {Boolean(payload.administratorLegalEntityClient?.id) &&
            Boolean(payload.legalEntityClient?.id) &&
            contextCompany && <PayexpressBanner jobRequest={payload} />}

          <DetailSection
            className={`${baseClass}__section`}
            isVisible={!additionalDetailsEmpty}
            label={t("JOB_REQUESTS.DETAIL.SECTION_HEADER_ADDITIONAL_DETAILS")}
            weight="bold">
            <div>
              <JobRequestAdditionalDetailSections payload={payload} />
            </div>
          </DetailSection>

          <DetailSection
            className={`${baseClass}__section`}
            isVisible={!attachmentsEmpty}
            label={t("JOB_REQUESTS.DETAIL.SECTION_HEADER_ATTACHMENTS")}
            weight="bold">
            <div>
              {authenticated ? (
                <AttachmentsList attachments={payload.attachments} padding={[false]} />
              ) : (
                <PublicAttachmentsList />
              )}
            </div>
          </DetailSection>

          <DetailSection
            className={`${baseClass}__section`}
            isVisible={!(customFieldsEmpty && referenceIdEmpty)}
            label={t("JOB_REQUESTS.DETAIL.SECTION_HEADER_CUSTOM_FIELDS")}
            weight="bold">
            <CustomFields customFields={payload.customFields}>
              {!referenceIdEmpty && (
                <CustomField
                  customField={{
                    name: t("JOB_REQUESTS.DETAIL.REFERENCE_ID"),
                    value: payload.businessReferenceId!,
                    type: "TEXT",
                    order: 0,
                  }}
                />
              )}
            </CustomFields>
          </DetailSection>

          <DetailSection
            hasSeparator
            isVisible={!contactsEmpty}
            className={`${baseClass}__section`}
            label={t("COMPONENTS.CONTACT_SECTION.HEADER", { name: administratorName })}
            weight="bold">
            <div>{payload.hiringManager && <ContactPersonDetail contactPerson={payload.hiringManager} />}</div>
          </DetailSection>

          <DetailSection
            hasSeparator
            isVisible={authenticated && payload.promoteClient && !originEmpty}
            className={`${baseClass}__section`}
            label={t("JOB_REQUESTS.DETAIL.MANAGED_BY", { name: administratorName })}
            weight="bold">
            <div>
              {authenticated && payload.promoteClient && payload.origin && (
                <JobRequestManagedByDetailSection jobRequest={payload} />
              )}
            </div>
          </DetailSection>

          {payload.brokerFee?.description && authenticated && (
            <InfoBox variant="information" text={payload.brokerFee.description} />
          )}
        </Flex>
        {!authenticated && !blurSwitch && <JobRequestPublicCreateAccountSection jobRequestId={payload.id} />}
      </CardSection>
    </Flex>
  );
};
